import { ICourse } from '@dtos/Course'
import { useGetCourse } from '@services/api/graphql/course'
import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useApolloErrorToast } from '@hooks/useApolloErrorToast'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

interface ICourseProviderProps {
  course?: ICourse
  isLoading: boolean
  error?: Error
  onLoadMore?: () => void
  resetCourse?: () => void
}

const NOOP = () => {}

export const CourseContext = createContext<ICourseProviderProps>({
  course: undefined,
  isLoading: false,
  error: undefined,
  onLoadMore: NOOP,
  resetCourse: NOOP,
})

export function CourseProvider({ children }: { children: ReactNode }) {
  const showApolloErrorToast = useApolloErrorToast()
  const { courseId } = useCourseRouteParams()
  const [course, setCourse] = useState<ICourse | undefined>(undefined)

  const { data, loading, error } = useGetCourse(`${courseId}`)

  const resetCourse = useCallback(() => {
    setCourse(undefined)
  }, [])

  useEffect(() => {
    if (data) {
      setCourse(data.course)
    }
  }, [data])

  useEffect(() => {
    if (error) {
      showApolloErrorToast(error, 'apolloErrors.course.title')
    }
  }, [error, showApolloErrorToast])

  const contextValue = useMemo(
    () => ({
      course,
      isLoading: loading,
      error,
      onLoadMore: NOOP,
      resetCourse,
    }),
    [course, loading, error, resetCourse],
  )

  return (
    <CourseContext.Provider value={contextValue}>
      {children}
    </CourseContext.Provider>
  )
}
